import type { SearchEndpointResponse } from '@ps/isp-web-services-types';

import { initialSearchResults } from '../model/defaultState';
import type { SearchResults } from '../model/types';

export function getEventsMatchingQuery(
  searchQuery: string,
  eventLinks: SearchEndpointResponse['eventLinks']
) {
  return eventLinks.filter((eventLink) => eventLink.text.toLowerCase().includes(searchQuery));
}

export function getMappedEventLinkToSearchMatches(
  eventLinks: SearchEndpointResponse['eventLinks'],
  competitionLinks: SearchEndpointResponse['competitionLinks']
): SearchResults['matches'] {
  return eventLinks.map((event) => ({
    alias: 'sportsEvent',
    link: {
      ...event,
      // TODO https://jira.pyrsoftware.ca/jira/browse/GSFET-1827
      competitionName: competitionLinks.find(
        (competitionLink) => competitionLink.competitionId === event.competitionId
      )?.text
    }
  }));
}

export function getCompetitionsMatchingQuery(
  searchQuery: string,
  competitionLinks: SearchEndpointResponse['competitionLinks']
) {
  return competitionLinks.filter((competitionLink) =>
    competitionLink.text.toLowerCase().includes(searchQuery)
  );
}

export function getMappedCompetitionLinksToSearchMatches(
  competitionLinks: SearchEndpointResponse['competitionLinks']
): SearchResults['matches'] {
  return competitionLinks.map((competition) => ({
    alias: 'sportsCompetition',
    link: {
      ...competition
    }
  }));
}

export function getUniqueEventTypes(
  links: SearchEndpointResponse['eventLinks'] | SearchEndpointResponse['competitionLinks'],
  eventTypeLinks: SearchEndpointResponse['eventTypeLinks']
) {
  const uniqueEventTypes: SearchEndpointResponse['eventTypeLinks'] = [];
  links.forEach(({ eventTypeId }) => {
    const isEventTypeIdInUnique = uniqueEventTypes.some(
      (eventType) => eventType.eventTypeId === eventTypeId
    );
    if (!isEventTypeIdInUnique) {
      const linkEventTypeInEventTypeLinks = eventTypeLinks.find(
        (eventTypeLink) => eventTypeLink.eventTypeId === eventTypeId
      );
      if (linkEventTypeInEventTypeLinks) {
        uniqueEventTypes.push(linkEventTypeInEventTypeLinks);
      }
    }
  });
  return uniqueEventTypes;
}

export const getSearchResults = (
  searchQuery: string,
  { eventLinks, competitionLinks, eventTypeLinks }: SearchEndpointResponse
): SearchResults => {
  const eventsByQuery = getEventsMatchingQuery(searchQuery, eventLinks);

  if (eventsByQuery.length) {
    return {
      uniqueEventTypes: getUniqueEventTypes(eventsByQuery, eventTypeLinks),
      matches: getMappedEventLinkToSearchMatches(eventsByQuery, competitionLinks)
    };
  }

  const competitionsByQuery = getCompetitionsMatchingQuery(searchQuery, competitionLinks);

  if (competitionsByQuery.length) {
    const uniqueEventTypes = getUniqueEventTypes(competitionsByQuery, eventTypeLinks);
    const searchMatches = getMappedCompetitionLinksToSearchMatches(competitionsByQuery);
    return {
      uniqueEventTypes,
      matches: searchMatches
    };
  }

  return initialSearchResults;
};
